import { Link, useLocation } from "react-router-dom";
import { MapPin, Users, BedDouble, Bath, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLang } from "@/context/LanguageContext";

export default function PropertyCard({ property }) {
  const { search } = useLocation();
  const sp = new URLSearchParams(search);
  const stay = new URLSearchParams();
  ["checkin", "checkout", "guests"].forEach((k) => { if (sp.get(k)) stay.set(k, sp.get(k)); });
  const href = `/proprieta/${property.slug}${stay.toString() ? `?${stay}` : ""}`;
  const { lang } = useLang();
  const en = lang === "en";
  const main = property.photos?.find((p) => p.is_main) || property.photos?.[0];
  const owner = property.owner;
  const rating = property.reviews?.length
    ? (property.reviews.reduce((a, r) => a + (r.rating || 0), 0) / property.reviews.length).toFixed(2)
    : null;

  return (
    <article
      data-testid="property-card-item"
      className="group bg-white rounded-2xl overflow-hidden border border-brand-plum/10 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col"
    >
      <Link to={href} className="block relative overflow-hidden aspect-[4/3]">
        <img
          src={main?.url}
          alt={main?.alt || property.name}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        {property.type && (
          <span className="absolute top-3 left-3 text-[11px] font-medium px-2.5 py-1 rounded-full uppercase tracking-wider bg-white/90 text-brand-plum backdrop-blur" data-testid="property-card-type">
            {property.type}
          </span>
        )}
        {property.discount_percent > 0 && (
          <span className="absolute top-3 right-3 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-brand-teal text-brand-violet">
            -{property.discount_percent}% {en ? "direct" : "diretto"}
          </span>
        )}
      </Link>

      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="font-serif text-xl text-brand-plum truncate">{property.name}</h3>
            <p className="flex items-center gap-1 text-sm text-brand-plum/50 mt-0.5">
              <MapPin size={13} className="text-brand-teal" /> {property.city}
              {property.neighborhood ? `, ${property.neighborhood}` : ""}
              {property.distance_km != null ? ` · ${en ? `${property.distance_km} km away` : `a ${property.distance_km} km`}` : ""}
            </p>
          </div>
          {rating && (
            <span className="flex items-center gap-1 text-sm font-semibold text-brand-plum shrink-0">
              <Star size={14} className="fill-brand-gold text-brand-gold" /> {rating}
            </span>
          )}
        </div>

        {owner && (
          <p className="mt-3 text-sm text-brand-plum/70">
            {en ? "Hosted by" : "Ospitato da"} <span className="font-semibold text-brand-plum">{owner.first_name}</span>
          </p>
        )}

        <div className="mt-3 flex flex-wrap gap-3 text-xs text-brand-plum/60">
          <span className="flex items-center gap-1"><Users size={13} /> {property.guests} {en ? "guests" : "ospiti"}</span>
          <span className="flex items-center gap-1"><BedDouble size={13} /> {property.bedrooms} {en ? "bedrooms" : "camere"}</span>
          <span className="flex items-center gap-1"><Bath size={13} /> {property.bathrooms} {en ? "bathrooms" : "bagni"}</span>
        </div>

        <div className="mt-auto pt-4 flex items-end justify-between">
          <div>
            <span className="text-[11px] text-brand-plum/50 block leading-none mb-0.5">{en ? "From" : "A partire da"}</span>
            <span className="font-serif text-2xl text-brand-plum">€{property.price_per_night}</span>
            <span className="text-sm text-brand-plum/50"> {en ? "/ night" : "/ notte"}</span>
          </div>
          <Link to={href} data-testid="property-card-view-btn">
            <Button className="rounded-full bg-brand-plum hover:bg-brand-violet text-white text-sm px-5">
              {en ? "View home" : "Scopri la casa"}
            </Button>
          </Link>
        </div>
      </div>
    </article>
  );
}
